import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { createHttpExtraOptions } from '@vision/ui/utils/http.utils';
import { axiosBaseQuery } from '../base.service';
import type {
  ChannelThemeAssetResponseDto,
  ChannelThemeResponseDto,
  ChannelThemesResponseDto,
} from './channel-themes.api.types';
import {
  fromApiChannelTheme,
  fromApiChannelThemeAsset,
  toApiChannelThemeAssetUpload,
  toApiChannelThemeAssetUploadUrl,
  toApiChannelThemeUrl,
  toApiChannelThemesUrl,
  toApiChannelThemeWrite,
} from './channel-themes.mapper';
import type { ThemeAssetReference } from '../theme-contract/types';
import type {
  AccountTheme,
  CloneChannelThemeArgs,
  CreateChannelThemeArgs,
  DeleteChannelThemeArgs,
  GetChannelThemesArgs,
  UpdateChannelThemeArgs,
  UploadChannelThemeAssetArgs,
} from './types';

// The theme editor reports a successful write on the selected theme in its preview header.
function toWriteOptions(notifySuccess = true) {
  return notifySuccess ? undefined : createHttpExtraOptions({ notification: { autoHandleSuccess: false } });
}

// The account themes the channels' Theme Designer selects from.
export const ChannelThemesService = createApi({
  reducerPath: 'ChannelThemesService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['ChannelThemes'],
  endpoints: (builder) => ({
    getChannelThemes: builder.query<AccountTheme[], GetChannelThemesArgs>({
      query: ({ accountId }) => ({
        url: toApiChannelThemesUrl(accountId),
        method: HttpMethod.GET,
      }),
      providesTags: ['ChannelThemes'],
      transformResponse: (response: ChannelThemesResponseDto) => response.data.map(fromApiChannelTheme),
    }),
    createChannelTheme: builder.mutation<AccountTheme, CreateChannelThemeArgs>({
      query: (args) => ({
        url: toApiChannelThemesUrl(args.accountId),
        method: HttpMethod.POST,
        data: toApiChannelThemeWrite(args),
        extraOptions: toWriteOptions(args.notifySuccess),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    updateChannelTheme: builder.mutation<AccountTheme, UpdateChannelThemeArgs>({
      query: (args) => ({
        url: toApiChannelThemeUrl(args.accountId, args.themeId, 'update'),
        method: HttpMethod.POST,
        data: toApiChannelThemeWrite(args),
        extraOptions: toWriteOptions(args.notifySuccess),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    cloneChannelTheme: builder.mutation<AccountTheme, CloneChannelThemeArgs>({
      query: ({ accountId, themeId }) => ({
        url: toApiChannelThemeUrl(accountId, themeId, 'clone'),
        method: HttpMethod.POST,
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
      transformResponse: (response: ChannelThemeResponseDto) => fromApiChannelTheme(response.data),
    }),
    // The API rejects deleting a theme assigned to any channel (HTTP 400).
    deleteChannelTheme: builder.mutation<void, DeleteChannelThemeArgs>({
      query: ({ accountId, themeId }) => ({
        url: toApiChannelThemeUrl(accountId, themeId, 'delete'),
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: { translationMessageKey: 'http.message.channel.deleteTheme.{{status}}' },
        }),
      }),
      invalidatesTags: (_result, error) => (error ? [] : ['ChannelThemes']),
    }),
    // A chosen image is uploaded right away; its thumbnail in the editor reports success.
    uploadChannelThemeAsset: builder.mutation<ThemeAssetReference, UploadChannelThemeAssetArgs>({
      query: ({ accountId, file }) => ({
        url: toApiChannelThemeAssetUploadUrl(accountId),
        method: HttpMethod.POST,
        data: toApiChannelThemeAssetUpload(file),
        extraOptions: toWriteOptions(false),
      }),
      transformResponse: (response: ChannelThemeAssetResponseDto) => fromApiChannelThemeAsset(response.data),
    }),
  }),
});

export const {
  useCloneChannelThemeMutation,
  useCreateChannelThemeMutation,
  useDeleteChannelThemeMutation,
  useGetChannelThemesQuery,
  useUpdateChannelThemeMutation,
  useUploadChannelThemeAssetMutation,
} = ChannelThemesService;
